import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { strengthPct } from "@/pages/candidate/profile/parts";
import { usePortalData, firstName } from "@/components/candidate_portal/portal";
import { NextStepCard, pickNextStep } from "@/components/candidate_portal/HomeNextStep";
import { HomeCopilot } from "@/components/candidate_portal/HomeCopilot";
import { HomeApplications } from "@/components/candidate_portal/HomeApplications";
import { ProfileStrengthCard, NewMatchesCard, AutoApplyCard, WeekCard } from "@/components/candidate_portal/HomeSidebar";
import { FirstRun, ProfileNotReady } from "@/components/candidate_portal/HomeFirstRun";

const Wrap = ({ children }) => <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="candidate-home">{children}</div>;

function HomeSkeleton() {
  return (
    <Wrap>
      <div role="status" aria-busy="true" className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-7">
        <span className="sr-only">Loading your workspace…</span>
        <div className="space-y-5"><Skeleton className="h-10 w-72" /><Skeleton className="h-28 w-full rounded-2xl" /><Skeleton className="h-24 w-full rounded-[14px]" />{[0, 1, 2].map((i) => <Skeleton key={i} className="h-[72px] w-full rounded-[14px]" />)}</div>
        <div className="space-y-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 w-full rounded-[14px]" />)}</div>
      </div>
    </Wrap>
  );
}

export default function CandidateAIHome() {
  const { user } = useAuth();
  const d = usePortalData();
  if (d.apps === undefined || d.profile === undefined) return <HomeSkeleton />;

  const apps = d.apps || [];
  const opps = d.opp?.opportunities || [];
  const name = firstName(user?.name);
  if (d.profile?.status !== "confirmed" && !user?.invitation_only) return <Wrap><ProfileNotReady status={d.profile?.status} /></Wrap>;
  if (apps.length === 0) return <Wrap><FirstRun name={name} opp={d.opp} pct={strengthPct(d.profile)} /></Wrap>;

  const toggleAuto = async (enabled) => {
    const a = d.auto || {};
    try {
      const { data } = await api.put("/autoapply", { enabled, min_fit: a.min_fit ?? 70, max_total: a.max_total ?? 5, work_modes: a.work_modes || [] });
      d.setData((s) => ({ ...s, auto: data }));
      toast.success(enabled ? "Auto-apply is on" : "Auto-apply paused");
    } catch { toast.error("Could not update auto-apply"); }
  };
  const testApp = apps.find((a) => ["not_started", "in_progress"].includes(a.assessment?.status));
  const hint = testApp ? `How should I prepare for the ${testApp.job?.title} assessment?` : "What should I focus on to land my next role?";

  return (
    <Wrap>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-7 items-start">
        <div className="space-y-6 min-w-0 hs-stagger">
          <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid="home-greeting">Welcome back{name ? `, ${name}` : ""}</h1>
          <NextStepCard step={pickNextStep(apps, d.interviews, opps)} />
          <HomeCopilot hint={hint} />
          <HomeApplications apps={apps} interviews={d.interviews} />
        </div>
        <aside className="space-y-4 hs-stagger" aria-label="Your search at a glance">
          <ProfileStrengthCard profile={d.profile} />
          <NewMatchesCard opp={d.opp} />
          <AutoApplyCard auto={d.auto} onToggle={toggleAuto} />
          <WeekCard apps={apps} interviews={d.interviews} opps={opps} />
        </aside>
      </div>
    </Wrap>
  );
}
